import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { KpiCard, AppsTable, SectionTitle } from "@/components/AppTable";
import { toast } from "sonner";

export default function Dashboard() {
  const [kpi, setKpi] = useState(null);
  const [newApps, setNewApps] = useState([]);
  const [lowRated, setLowRated] = useState([]);
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [k, n, l, d] = await Promise.all([
        api.get("/apps-stats/kpi"),
        api.get("/apps?first_seen_days=30&sort=first_seen&order=desc&page_size=10"),
        api.get("/apps?max_rating=3.5&sort=rating&order=asc&page_size=10"),
        api.get("/apps-stats/rating-drops?limit=10"),
      ]);
      setKpi(k.data);
      setNewApps(n.data.items || []);
      setLowRated(l.data.items || []);
      setDrops(d.data || []);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cards = [
    { label: "Total Apps Tracked", value: kpi?.total_apps ?? "—", testid: "kpi-total" },
    { label: "New Apps · 7d", value: kpi?.new_7d ?? "—", testid: "kpi-new-7d" },
    { label: "New Apps · 30d", value: kpi?.new_30d ?? "—", testid: "kpi-new-30d" },
    { label: "Low Rated", value: kpi?.low_rated ?? "—", testid: "kpi-low-rated", sub: "Rating < 3.5" },
    { label: "Rating Drops", value: kpi?.rating_drops ?? "—", testid: "kpi-drops" },
    { label: "Recently Updated", value: kpi?.recently_updated ?? "—", testid: "kpi-updated", sub: "Last 7 days" },
    { label: "High-Potential Leads", value: kpi?.high_potential ?? "—", testid: "kpi-leads", sub: "Score ≥ 70" },
    { label: "Developers Tracked", value: kpi?.developers ?? "—", testid: "kpi-developers" },
  ];

  return (
    <div className="p-8 space-y-10">
      <div>
        <div className="overline-label text-muted-foreground">Overview</div>
        <h1 className="font-heading font-black text-4xl mt-1">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 grid-borders border-l border-t border-border">
        {cards.map((c) => <KpiCard key={c.label} {...c} />)}
      </div>

      <section>
        <SectionTitle eyebrow="Just discovered" title="New Apps" />
        <AppsTable apps={newApps} />
      </section>

      <section>
        <SectionTitle eyebrow="Rating < 3.5" title="Low Rated Apps" />
        <AppsTable apps={lowRated} />
      </section>

      <section>
        <SectionTitle eyebrow="Recent movement" title="Rating Drops" />
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 border-b border-border">
              <tr className="text-left overline-label text-muted-foreground">
                <th className="px-3 py-2.5">App</th>
                <th className="px-3 py-2.5">Developer</th>
                <th className="px-3 py-2.5 text-right">Previous</th>
                <th className="px-3 py-2.5 text-right">Current</th>
                <th className="px-3 py-2.5 text-right">Change</th>
                <th className="px-3 py-2.5 text-right">Reviews +</th>
              </tr>
            </thead>
            <tbody>
              {drops.length ? drops.map((d) => (
                <tr key={d.package_name} className="border-b border-border/60 hover:bg-accent/50">
                  <td className="px-3 py-2.5">
                    <a href={`/apps/${d.package_name}`} className="font-medium hover:underline">{d.name}</a>
                    <div className="font-mono text-[10px] text-muted-foreground">{d.package_name}</div>
                  </td>
                  <td className="px-3 py-2.5">{d.developer}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{d.prev_rating?.toFixed(1)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{d.current_rating?.toFixed(1)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-red-600">{d.rating_change?.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">+{d.review_growth}</td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground text-sm">
                  No rating drops detected yet — snapshots accumulate over time.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
